import { MdOutlineTimer } from 'react-icons/md';
import { SquashDetails } from '@src/components/SquashDetails';
import { BackgroundHighRes } from '@src/components/BackgroundHighRes.js';
import { SquashHighRes } from '@src/components/SquashHighRes.js';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Button, HStack, Spinner, VStack, Text } from 'ui_2';
import { useSquash, useSquashWater, useSquashTime } from '@src/hooks/useSquash';
import { useMemo } from 'react';
import { getRecords } from '@puzzlehq/sdk';
import { Route as MintRoute } from './mint.js';
import { SelectedAccount } from '@src/components/SelectedAccount.js';
import { LoadingMessage } from '@src/components/LoadingMessage.js';
import z from 'zod';
import WateringAnimation from '@src/components/WateringAnimation.js';
import { SunRising } from '@src/components/SunRising.js';
import { IncorrectNetworkModal } from '@src/components/IncorrectNetworkDialog.js';
import { SquashMoodView } from '@src/components/SquashMood.js';
import { FeeView } from '@src/components/FeeBannerView.js';
import { MuteButton } from '@src/components/MuteButton.js';
import { ArcadeStorePrompt, useFeeValid } from '@puzzlehq/frontend-utils';
import useWindowDimensions from '@src/hooks/useWindowDimensions';
import debug from 'debug';
import { extractAmountFromInputString } from 'jigsaw';
import { useSquashGameState } from '@src/providers/squashGameState.js';
import { useGameAudio } from '@src/hooks/useAudio.js';
import { SquashHistoryButton } from '@src/components/SquashHistoryButton.js';

const log_game_index = debug('route:game_index');
log_game_index.enabled = true;

const gameIndexSearchSchema = z.object({
  waterEventId: z.string().optional(),
  expectedRecordCount: z.number().int().positive().optional(),
  finishingGame: z.boolean().optional(),
});

type WaterState = 'watering' | 'needsWater' | 'watered';

type WaterButtonProps = {
  waterState: WaterState;
  timeRemainingStr?: string;
  readyToWater?: boolean;
  readyToLevelUp?: boolean;
  needsWater?: boolean;
  isWatering?: boolean;
  gameIsActive?: boolean;
  feeValid?: boolean;
  water: () => void;
};

const WaterButton = ({
  waterState,
  timeRemainingStr,
  readyToLevelUp,
  readyToWater,
  needsWater,
  isWatering,
  feeValid,
  water,
  gameIsActive,
}: WaterButtonProps) => {
  return (
    <VStack className='z-30 flex items-center justify-center'>
      {waterState === 'needsWater' && gameIsActive && (
        <Button
          className='z-30 h-[4rem] gap-2 font-body text-[24px] font-normal text-black'
          disabled={!needsWater || !readyToWater || isWatering || !feeValid}
          onClick={water}
          variant='squashPrimary'
        >
          <VStack className='items-center justify-center'>
            <HStack>
              <span role='img' className='mr-2'>
                {String.fromCodePoint(0x1f4a6)}
              </span>
              {feeValid === false
                ? 'Low balance'
                : readyToLevelUp
                ? 'Level Up'
                : 'Water'}
            </HStack>
            <HStack className='items-center justify-center gap-2'>
              <MdOutlineTimer className='text-sm text-gray-600' />
              <Text font='light-sm' color='gray-600'>
                {timeRemainingStr}
              </Text>
            </HStack>
          </VStack>
          {isWatering && <Spinner />}
        </Button>
      )}
      {waterState === 'watering' && (
        <Button
          variant='gray'
          className={`z-30 gap-2 font-body ${
            readyToLevelUp ? 'text-[18px]' : 'text-[24px]'
          } font-normal`}
          disabled={true}
        >
          <span role='img' className='mr-2'>
            {String.fromCodePoint(0x1f4a6)}
          </span>
          {readyToLevelUp ? 'Leveling up' : 'Watering'}
          {isWatering && <Spinner />}
        </Button>
      )}
      {waterState === 'watered' && (
        <Button
          variant='gray'
          disabled={true}
          className='z-30 h-[4rem] gap-2 font-body text-[24px] font-normal text-black'
        >
          <VStack className='items-center justify-center'>
            <HStack>
              <span role='img' className='mr-2'>
                {String.fromCodePoint(0x1f44d)}
              </span>
              Watered
            </HStack>
            <HStack className='items-center gap-2'>
              <MdOutlineTimer className='text-sm' />
              <Text font='light-sm'>until {timeRemainingStr}</Text>
            </HStack>
          </VStack>
        </Button>
      )}
    </VStack>
  );
};

export const Route = createFileRoute('/game/')({
  validateSearch: gameIndexSearchSchema,
  beforeLoad: async () => {
    const squashRecords = await getRecords({
      filter: {
        status: 'All',
        programIds: [import.meta.env.VITE_SQUASH_PROGRAM_ID],
      },
    });
    const squashMintRecords = squashRecords.records.filter((r) =>
      ['mint', 'migrate_from_v3', 'mint_arbitrary_squash'].includes(
        r.functionId
      )
    );
    log_game_index('squashMintRecords', squashMintRecords);

    const completedSquashRecordIds = squashRecords.records
      .filter((r) => {
        const level = extractAmountFromInputString(r?.data?.level);
        return (
          ['water_and_level_up'].includes(r.functionId) &&
          level !== null &&
          level >= 5
        );
      })
      .map((r) => r.data.squash_id as string);
    log_game_index('completedSquashRecordIds', completedSquashRecordIds);

    const activeSquashMintRecord = squashMintRecords?.find((record) => {
      if (completedSquashRecordIds.includes(record.data.squash_id as string))
        return false;
      const level = extractAmountFromInputString(record?.data?.level);
      return level !== null && level < 5;
    });
    log_game_index('activeSquashMintRecord', activeSquashMintRecord);

    if (activeSquashMintRecord === undefined) {
      // you're not supposed to be here...
      log_game_index(
        '/game/index squashMintRecord is undefined! Redirecting to mint route'
      );
      throw redirect({
        to: MintRoute.to,
        search: (prev) => ({
          finishingGame: prev.finishingGame,
        }),
      });
    }
    return true;
  },
  pendingComponent: () => <LoadingMessage message='Loading your squash' />,
  component: function Component() {
    const { account, activeSquash } = useSquashGameState();
    const { timeRemainingStr } = useSquash();
    const { gameIsActive } = useSquashTime();
    const { readyToWater, readyToLevelUp, needsWater, isWatering, water } =
      useSquashWater();
    const { height } = useWindowDimensions();

    const waterState = useMemo(() => {
      return needsWater ? (isWatering ? 'watering' : 'needsWater') : 'watered';
    }, [needsWater, readyToWater, isWatering]);

    useGameAudio(activeSquash, isWatering);

    const feeValid = useFeeValid({
      functionId: readyToLevelUp ? 'water_and_level_up' : 'water',
      programId: import.meta.env.VITE_SQUASH_PROGRAM_ID!,
      network: import.meta.env.VITE_NETWORK!,
    });

    return (
      <VStack className='relative h-full w-full max-w-[478px] flex-none items-center bg-squashBg font-body'>
        <div className='absolute inset-0 flex h-full w-full items-center justify-center'>
          <div className='relative h-full w-full'>
            <BackgroundHighRes />
            <SquashHighRes />
            <WateringAnimation isWatering={isWatering} />
            {gameIsActive && (
              <VStack
                className={`absolute ${
                  height > 700 ? 'bottom-[115px]' : 'bottom-[90px]'
                } left-1/2 z-30 -translate-x-1/2 items-center gap-2`}
              >
                <WaterButton
                  waterState={waterState}
                  water={water}
                  gameIsActive={gameIsActive}
                  isWatering={isWatering}
                  needsWater={needsWater}
                  readyToLevelUp={readyToLevelUp}
                  readyToWater={readyToWater}
                  timeRemainingStr={timeRemainingStr}
                  feeValid={feeValid}
                />
                <FeeView
                  functionId={readyToLevelUp ? 'water_and_level_up' : 'water'}
                />
              </VStack>
            )}
          </div>
          {gameIsActive === false && <SunRising />}
          <HStack className='absolute top-0 z-40 mt-4 w-full px-4'>
            <VStack className='gap-1'>
              <Text
                font='bold-lg'
                family='atak'
                className='[-webkit-text-stroke:2px_white] [paint-order:stroke_fill] [stroke-width:2px] [stroke:2px_white] [text-stroke:2px_white]'
              >
                {activeSquash?.squashName}
              </Text>
              <VStack className='gap-2'>
                {activeSquash && <SquashMoodView mood={activeSquash.mood} />}
              </VStack>
            </VStack>
            <HStack className='flex-grow' />
            <VStack className='items-end gap-2'>
              {account && <SelectedAccount account={account} />}
              <HStack className='items-center gap-2'>
                <SquashHistoryButton />
                <MuteButton />
              </HStack>
            </VStack>
          </HStack>
        </div>
        <SquashDetails />
        <IncorrectNetworkModal />
        <ArcadeStorePrompt />
      </VStack>
    );
  },
});
